import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  getPublishedLegalDocuments,
  LegalDocument,
  LegalDocumentType,
} from "../services/legalService";
import {
  FileText,
  Shield,
  AlertTriangle,
  Loader2,
  AlertCircle,
  RefreshCw,
} from "lucide-react";

type ActiveTab = "TERMS_AND_CONDITIONS" | "PRIVACY_POLICY" | "MEDICAL_DISCLAIMER";

const TAB_CONFIG: {
  key: ActiveTab;
  label: string;
  shortLabel: string;
  icon: React.ComponentType<{ className?: string }>;
  docType: LegalDocumentType;
}[] = [
  {
    key: "TERMS_AND_CONDITIONS",
    label: "Terms & Conditions",
    shortLabel: "Terms",
    icon: FileText,
    docType: "TERMS_AND_CONDITIONS",
  },
  {
    key: "PRIVACY_POLICY",
    label: "Privacy Policy",
    shortLabel: "Privacy",
    icon: Shield,
    docType: "PRIVACY_POLICY",
  },
  {
    key: "MEDICAL_DISCLAIMER",
    label: "Medical Disclaimer",
    shortLabel: "Medical",
    icon: AlertTriangle,
    docType: "MEDICAL_DISCLAIMER",
  },
];

const formatDocType = (type: string): string => {
  switch (type) {
    case "TERMS_AND_CONDITIONS":
      return "Terms & Conditions";
    case "PRIVACY_POLICY":
      return "Privacy Policy";
    case "MEDICAL_DISCLAIMER":
      return "Medical Disclaimer";
    default:
      return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }
};

const renderLegalContent = (rawContent: string) => {
  if (!rawContent) return null;

  // Clean empty headers/paragraphs at the start (e.g. <h1><br></h1>)
  const cleaned = rawContent
    .replace(/^(\s*<h[1-6]>\s*(<br\s*\/?>)?\s*<\/h[1-6]>)+/i, "")
    .replace(/^(\s*<p>\s*(<br\s*\/?>)?\s*<\/p>)+/i, "");

  const hasHtml = /<[a-z][\s\S]*>/i.test(cleaned);

  if (hasHtml) {
    return (
      <div
        className="legal-content-body text-neutral-800 text-sm leading-relaxed"
        dangerouslySetInnerHTML={{ __html: cleaned }}
      />
    );
  }

  return (
    <div className="text-neutral-800 text-sm leading-relaxed whitespace-pre-wrap font-sans">
      {cleaned}
    </div>
  );
};

const parseTabParam = (param: string | null | undefined): ActiveTab => {
  if (!param) return "TERMS_AND_CONDITIONS";
  const normalized = param.toUpperCase().replace(/-/g, "_");
  if (normalized === "PRIVACY" || normalized === "PRIVACY_POLICY") {
    return "PRIVACY_POLICY";
  }
  if (
    normalized === "MEDICAL" ||
    normalized === "MEDICAL_DISCLAIMER" ||
    normalized === "DISCLAIMER"
  ) {
    return "MEDICAL_DISCLAIMER";
  }
  return "TERMS_AND_CONDITIONS";
};

const LegalDocumentsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Determine active tab from URL param or default to TERMS_AND_CONDITIONS
  const [activeTab, setActiveTab] = useState<ActiveTab>(() =>
    parseTabParam(searchParams.get("tab"))
  );

  // Sync activeTab whenever URL query changes (e.g. user navigates to /legal?tab=medical-disclaimer)
  useEffect(() => {
    const tabFromUrl = parseTabParam(searchParams.get("tab"));
    setActiveTab(tabFromUrl);
  }, [searchParams]);

  // Sync tab with URL
  const handleTabChange = (tab: ActiveTab) => {
    setActiveTab(tab);
    setSearchParams({ tab: tab.toLowerCase().replace(/_/g, "-") });
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const docsData = await getPublishedLegalDocuments();
      setDocuments(Array.isArray(docsData) ? docsData : []);
    } catch (err: any) {
      console.error("Failed to fetch legal documents:", err);
      setError("Unable to load legal documents. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Current document for selected tab
  const currentDoc = documents.find((d) => d.documentType === activeTab) || null;

  return (
    <div className="w-full space-y-6 pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Legal Documents</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Review the current Terms &amp; Conditions, Privacy Policy, and Medical Disclaimer.
          </p>
        </div>

        {/* Read-Only Badge */}
        <div className="flex items-center space-x-2 text-xs text-neutral-500 bg-neutral-100 border border-neutral-200 rounded-full px-3 py-1">
          <Shield className="h-3.5 w-3.5 text-primary-600" />
          <span>Platform Source of Truth &bull; Read-Only</span>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-neutral-200">
        <div className="flex overflow-x-auto no-scrollbar space-x-2 sm:space-x-4">
          {TAB_CONFIG.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`flex items-center space-x-2 py-3 px-3 sm:px-4 border-b-2 font-medium text-sm whitespace-nowrap transition-colors duration-150 cursor-pointer ${
                  isActive
                    ? "border-primary-600 text-primary-600 font-semibold"
                    : "border-transparent text-neutral-600 hover:text-neutral-900 hover:border-neutral-300"
                }`}
              >
                <Icon
                  className={`h-4 w-4 ${
                    isActive ? "text-primary-600" : "text-neutral-400"
                  }`}
                />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.shortLabel}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content Area */}
      {isLoading ? (
        <div className="card flex flex-col items-center justify-center py-24 space-y-4">
          <Loader2 className="h-9 w-9 animate-spin text-primary-600" />
          <p className="text-sm text-neutral-500">Loading legal document content...</p>
        </div>
      ) : error ? (
        <div className="card flex flex-col items-center justify-center py-16 space-y-4 text-center">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center text-red-600">
            <AlertCircle className="h-6 w-6" />
          </div>
          <div className="max-w-md">
            <h3 className="text-base font-semibold text-neutral-900">Failed to Load Documents</h3>
            <p className="text-sm text-neutral-500 mt-1">{error}</p>
          </div>
          <button
            onClick={fetchData}
            className="btn-primary text-sm flex items-center space-x-2 px-4 py-2 mt-2 cursor-pointer"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Try Again</span>
          </button>
        </div>
      ) : currentDoc ? (
        /* Published Legal Document Viewer */
        <div className="card">
          {/* Metadata Card Header */}
          <div className="pb-5 border-b border-neutral-200">
            <h2 className="text-xl font-bold text-neutral-900">{currentDoc.title}</h2>
          </div>

          {/* Document Content */}
          <div className="max-w-none pt-4">
            {renderLegalContent(currentDoc.content)}
          </div>

          {/* Footer Metadata */}
          <div className="pt-6 border-t border-neutral-200 flex items-start sm:items-center text-xs text-neutral-400 gap-2">
            <span className="italic">
              Official document managed and published by Platform Super Admin.
            </span>
          </div>
        </div>
      ) : (
        /* Empty State */
        <div className="card flex flex-col items-center justify-center py-20 text-center space-y-3">
          <FileText className="h-10 w-10 text-neutral-300" />
          <h3 className="text-base font-semibold text-neutral-900">
            No Published Document Available
          </h3>
          <p className="text-sm text-neutral-500 max-w-md">
            There is currently no published version of{" "}
            <span className="font-medium text-neutral-700">
              {formatDocType(activeTab)}
            </span>{" "}
            available on the platform.
          </p>
        </div>
      )}
    </div>
  );
};

export default LegalDocumentsPage;
